import {
  Component,
  Inject,
  OnInit,
  OnDestroy,
  isDevMode,
  ChangeDetectorRef,
} from '@angular/core';
import { AgentEntity } from '@wephone-core/model/entity/agent';
import { AgentRepository } from '@wephone-core/model/repository/agent';
import { IvrRemoteAppRepository } from '@wephone-core/model/repository/ivr_remote_app';
import { DialogType, TransferDestinationType, TransferRequest } from '@wephone-core/core/call_transfer';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatTabChangeEvent } from '@angular/material/tabs';
import { EntityManager } from '@wephone-core/wephone-core.module';
import { FormControl } from '@angular/forms';
import { AuthenticationService } from '@wephone-core/service/authentication';
import { PhoneService } from '@wephone-app-phone/services';
import { QueueTransferNumberEntity } from '@wephone-core/model/entity/queue_transfernumber';
import { DialogComponentBase } from '@wephone-core-ui';
import { DialogActionButton, Colors, regexSearch } from '@wephone-utils';
import { _tk, _ti } from '@wephone-translation';
import { TranslateService } from '@ngx-translate/core';
import { MatSlideToggleChange } from '@angular/material/slide-toggle';
import { TagEntity } from '@wephone-core/model/entity/tag';
import { TagRepository } from '@wephone-core/model/repository/tag';
import * as _ from 'lodash';

@Component({
  selector: 'call-transfer-modal',
  templateUrl: 'call-transfer.html',
  styleUrls: ['./call-transfer.scss']
})
export class CallTransferModal extends DialogComponentBase implements OnInit, OnDestroy {
  dialogTitle = _tk('call_transfer.title');
  backButton: DialogActionButton = {
    label: _tk('call_transfer.dialogs.transfer.back'),
    action: () => {
      this.prevStep();
    },
    visible: () => {
      return this.stepActive === 2;
    }
  };
  dialogRightActions: DialogActionButton[] = [
    {
      label: _tk('call_transfer.dialogs.transfer.blind_transfer'),
      action: () => {
        this.confirmTransfer(false);
      },
      visible: () => {
        return this.dialogType === DialogType.CALL_TRANSFER && this.stepActive === 2;
      },
      color: Colors.PRIMARY
    },
    {
      label: _tk('call_transfer.dialogs.transfer.assisted_transfer'),
      action: () => {
        this.confirmTransfer(true);
      },
      visible: () => {
        return this.isDialogTransferCall() && this.stepActive === 2;
      },
      color: Colors.PRIMARY
    },
    {
      label: _tk('call_transfer.dialogs.transfer.add_participant'),
      action: () => {
        this.confirmAddParticipant();
      },
      visible: () => {
        return this.dialogType === DialogType.ADD_PARTICIPANT;
      },
      color: Colors.PRIMARY
    },
  ];

  groupByTag = false;
  agent: AgentEntity;
  transferApps: any[] = [];
  result: TransferRequest;
  TransferDestinationType = TransferDestinationType;
  stepActive = 1;
  agents: AgentEntity[] = [];
  tags: TagEntity[] = [];
  filterCtrl = new FormControl();

  selectedAgent: AgentEntity;
  selectedTransferNumber: QueueTransferNumberEntity;
  selectedTransferApp: any;
  tabSelectedIndex = 0;

  private readonly agentRepo: AgentRepository;
  private readonly tagRepo: TagRepository;
  filter_value: string;
  other_number: string;
  devMode = false;

  dialogType: DialogType;
  other_number_error: boolean;

  constructor(
    @Inject(MAT_DIALOG_DATA) public dialogData: {
      type?: DialogType,
      predefined_numbers?: string[]
    },
    dialogRef: MatDialogRef<CallTransferModal>,
    private readonly authService: AuthenticationService,
    private readonly em: EntityManager,
    private readonly phoneService: PhoneService,
    cdr: ChangeDetectorRef,
  ) {
    super(dialogRef, cdr);
    this.devMode = isDevMode();
    this.agentRepo = this.em.getRepository('AgentRepository');
    this.tagRepo = this.em.getRepository('TagRepository');
    this.result = new TransferRequest(TransferDestinationType.USER);
    this.agent = this.agentRepo.getObjectById(this.authService.getAgentId());

    IvrRemoteAppRepository.getInstance().findAll(false).then(object_list => {
      object_list.forEach(object => {
        if (object.in_agent_transfer_dialog === 1) {
          this.transferApps.push({ label: object.name, name: object.alias, params: {} });
        }
      });
    });
  }

  async resolveData(): Promise<void> {
    await this.tagRepo.loadObjectList();
    this.tags = this.tagRepo.getObjectList();
  }

  ngOnInit(): void {
    super.ngOnInit();
    this.em.requestCallCenterLiveData();
    this.agents = this.agentRepo.getObjectList();
    this.dialogType = this.dialogData.type;
  }

  ngOnDestroy(): void {
    super.ngOnDestroy();
    this.em.releaseCallCenterLiveData();
  }

  onTabChange(event: MatTabChangeEvent): void {
    this.filter_value = undefined;
    this.tabSelectedIndex = event.index;
    switch (event.index) {
      case 0:
        this.result.destination_type = TransferDestinationType.USER;
        this.result.destination = this.selectedAgent;
        break;
      case 1:
        this.result.destination_type = TransferDestinationType.NUMBER;
        this.result.destination = this.selectedTransferNumber;
        break;
      case 2:
        this.result.destination_type = TransferDestinationType.APPLICATION;
        this.result.destination = this.selectedTransferApp;
        break;
      default:
        this.result.destination_type = TransferDestinationType.USER;
        this.result.destination = this.selectedAgent ? this.selectedAgent.user : undefined;
    }
  }

  onChangeGroupBy(ob: MatSlideToggleChange): void {
    this.groupByTag = ob.checked;
    this.detectChanges();
  }

  get filtered_online_agents(): AgentEntity[] {
    return this.agents.filter((agent: AgentEntity) => {
      if (this.filter_value && agent.name && !regexSearch(agent.name, this.filter_value.trim())) {
        return false;
      }
      return agent.enabled && (agent.is_online_cc || agent.is_online_calendar) && agent.id !== this.agent.id;
    });
  }

  get filtered_available_agents(): AgentEntity[] {
    const angets_available = this.agents.filter((agent: AgentEntity) => {
      if (this.filter_value && agent.name && !regexSearch(agent.name, this.filter_value.trim())) {
        return false;
      }

      return agent.enabled &&
        (agent.is_online_cc || agent.is_online_calendar) &&
        !agent.is_busy &&
        agent.id !== this.agent.id;
    });

    return _.orderBy(angets_available, ['name'], ['asc']);
  }

  get filtered_busy_agents(): AgentEntity[] {
    const angets_busy: AgentEntity[] = this.agents.filter((agent: AgentEntity) => {
      const ret: boolean = (agent.is_online_cc || agent.is_online_calendar) &&
        agent.is_busy &&
        agent.enabled &&
        agent.id !== this.agent.id;

      const filterValue: string = this.filter_value ? this.filter_value.trim() : this.filter_value;
      if (filterValue) {
        return ret && agent.name && regexSearch(agent.name, filterValue);
      }
      return ret;
    });

    return _.sortBy(angets_busy, ['name'], ['asc']);
  }

  get filtered_available_tags(): TagEntity[] {
    const available_tags = this.tags.filter(t => !!this.filtered_online_agents.find(a => Array.isArray(a.tags) && a.tags.includes(t.id)));
    return _.orderBy(available_tags, ['name'], ['asc']);
  }

  get_agents_by_tagId(tagId: number): AgentEntity[] {
    const tag_agents = this.filtered_online_agents.filter(agent => Array.isArray(agent.tags) && agent.tags.includes(tagId));
    return _.orderBy(tag_agents, ['name'], ['asc']);
  }

  get get_others_tag_agents(): AgentEntity[] {
    const other_tag_agents = this.agents.filter((agent: AgentEntity) => {
      if (this.filter_value && agent.name && !regexSearch(agent.name, this.filter_value.trim())) {
        return false;
      }
      return agent.enabled && (agent.is_online_cc || agent.is_online_calendar) && agent.id !== this.agent.id && _.isEmpty(agent.tags);
    });
    return _.orderBy(other_tag_agents, ['name'], ['asc']);
  }

  get isTranferNumber(): boolean {
    if (!this.phoneService.activeCall) {
      return false;
    }

    if (!this.phoneService.activeCall.queue) {
      return false;
    }

    if (_.isEmpty(this.phoneService.activeCall.queue.transfer_numbers)) {
      return false;
    }

    return true;
  }

  get transfer_numbers(): QueueTransferNumberEntity[] {
    if (!this.phoneService.activeCall) {
      return [];
    }

    if (!this.phoneService.activeCall.queue) {
      return [];
    }

    const transfer_numbers = (this.phoneService.activeCall.queue.transfer_numbers || [])
      .filter((transferNumber: QueueTransferNumberEntity) => {
        const filterValue: string = (this.filter_value || '').trim();
        if (!this.filter_value || !filterValue) {
          return true;
        }

        return (transferNumber.label && regexSearch(transferNumber.label, filterValue)) ||
          (transferNumber.phone_number && regexSearch(transferNumber.phone_number, filterValue));
      });

    return _.sortBy(transfer_numbers, [(tn: QueueTransferNumberEntity) => tn.label]);
  }

  get filtered_transfer_apps(): any[] {
    let transfer_apps = this.transferApps;
    const filterValue: string = (this.filter_value || '').trim();
    if (this.filter_value && filterValue) {
      transfer_apps = this.transferApps.filter(app => {
        return regexSearch(app.label, filterValue);
      });
    }

    return _.orderBy(transfer_apps, ['label'], ['asc']);
  }

  selectAgent(agent: AgentEntity): void {
    this.resetSelected();
    this.selectedAgent = agent;
    this.result.destination_type = TransferDestinationType.USER;
    this.result.destination = agent.user;
    this.gotoStep(2);
  }

  selectNumber(transferNumber: QueueTransferNumberEntity): void {
    this.resetSelected();
    this.other_number = undefined;
    this.selectedTransferNumber = transferNumber;
    this.result.destination = transferNumber.phone_number;
    this.gotoStep(2);
  }

  selectOtherNumber(): void {
    this.other_number_error = !this.other_number;
    if (this.other_number_error) {
      return;
    }
    this.resetSelected();
    this.result.destination = this.other_number;
    this.gotoStep(2);
  }

  selectApp(app: any): void {
    this.resetSelected();
    this.selectedTransferApp = app;
    this.result.destination = app;
    this.gotoStep(2);
  }

  private resetSelected(): void {
    this.selectedAgent = undefined;
    this.selectedTransferNumber = undefined;
    this.selectedTransferApp = undefined;
  }

  private gotoStep(step: number): void {
    if (this.dialogType === DialogType.ADD_PARTICIPANT) {
      return;
    }

    this.stepActive = step;
  }

  prevStep(): void {
    this.stepActive -= this.stepActive > 1 ? 1 : 0;
  }

  confirmTransfer(assisted: boolean): void {
    this.result.assisted = assisted;
    this.dismiss(this.result);
  }

  confirmAddParticipant(): void {
    if (this.result.destination_type === TransferDestinationType.NUMBER) {
      this.selectOtherNumber();
    }
    if (!this.result.destination) {
      return;
    }
    this.dismiss(this.result);
  }

  isDialogTransferCall(): boolean {
    return this.dialogType === DialogType.CALL_TRANSFER;
  }
}
